import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Button, Paper } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

/**
 * RuleSetHeader - Displays rule set information and back navigation
 * 
 * Shows rule set name and key information with a back button
 * 
 * Requirements: 4.1, 5.4
 * 
 * @param {Object} props
 * @param {Object} props.ruleset - The rule set object
 * @param {string} props.ruleset.name - The rule set's name
 * @param {string} props.ruleset.shortCode - The rule set's short code
 * @param {boolean} props.ruleset.isActive - Whether the rule set is active
 * @param {Function} props.onBack - Callback when back button is clicked
 */
const RuleSetHeader = ({ ruleset, onBack }) => {
  if (!ruleset) {
    return null;
  }

  return (
    <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={onBack}
          variant="outlined"
          size="small"
          sx={{ mr: 2 }}
          aria-label="Back to customer detail"
        >
          Back
        </Button>
      </Box>
      
      <Box>
        <Typography variant="h4" component="h1" gutterBottom>
          {ruleset.name}
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              Short Code
            </Typography>
            <Typography variant="body1">
              {ruleset.shortCode}
            </Typography>
          </Box>
          
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              Status
            </Typography>
            <Typography 
              variant="body1" 
              color={ruleset.isActive ? 'success.main' : 'error.main'}
            >
              {ruleset.isActive ? 'Active' : 'Inactive'}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

RuleSetHeader.propTypes = {
  ruleset: PropTypes.shape({
    name: PropTypes.string.isRequired,
    shortCode: PropTypes.string.isRequired,
    isActive: PropTypes.bool.isRequired
  }),
  onBack: PropTypes.func.isRequired
};

export default RuleSetHeader;
